import { Component, computed, OnInit, signal, WritableSignal, inject } from '@angular/core';
import { CardModule } from 'primeng/card';
import { AsyncPipe, DatePipe, SlicePipe } from '@angular/common';
import { ListboxModule } from 'primeng/listbox';
import { CodePipe } from '../shared/pipes/code.pipe';
import { Customer } from '../customer-overview/customer.model';
import { CustomerService } from '../customer-overview/customer.service';
import { HouseholdPipe } from '../shared/pipes/household.pipe';
import { isAfter, isSameDay, subDays, subYears } from 'date-fns';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { AgeType, FamilyMember } from '../customer-overview/add-edit-customer/member-form/family-member.model';
import { SettingsService } from '../administration/settings.service';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { PickUpService } from './pick-up.service';
import { PanelModule } from 'primeng/panel';
import { absenceStatuses } from '../absences-overview/absence.model';
import { ProgressBarModule } from 'primeng/progressbar';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { FloatLabelModule } from 'primeng/floatlabel';
import { StyleClassModule } from 'primeng/styleclass';
import { TextareaModule } from 'primeng/textarea';
import { AuthService } from '../login/auth.service';
import { AgesListPipe } from '../shared/pipes/ages-list.pipe';
import { RoleEnum } from '../login/role.enum';
import { ConfirmationService, MessageService } from 'primeng/api';
import { LocationService } from '../administration/location/location.service';
import { LocationModel } from '../administration/location/location.model';
import { DrawerModule } from 'primeng/drawer';
import { PickupChatComponent } from './pickup-chat/pickup-chat.component';
import { ConfirmDialogModule } from 'primeng/confirmdialog';

interface GroupedCustomers {
  time: string;
  customers: Customer[];
}

@Component({
  selector: 'app-pick-up',
  imports: [
    CardModule,
    DatePipe,
    ListboxModule,
    CodePipe,
    SlicePipe,
    AsyncPipe,
    HouseholdPipe,
    ProgressSpinnerModule,
    FormsModule,
    InputTextModule,
    ButtonModule,
    TagModule,
    PanelModule,
    ProgressBarModule,
    IconFieldModule,
    InputIconModule,
    FloatLabelModule,
    StyleClassModule,
    TextareaModule,
    RouterLink,
    AgesListPipe,
    PickupChatComponent,
    DrawerModule,
    ConfirmDialogModule
  ],
  templateUrl: './pick-up.component.html',
  styleUrl: './pick-up.component.scss',
  standalone: true
})
export class PickUpComponent implements OnInit {
  customerService = inject(CustomerService);
  private activatedRoute = inject(ActivatedRoute);
  settingsService = inject(SettingsService);
  pickupService = inject(PickUpService);
  authService = inject(AuthService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private locationService = inject(LocationService);

  today = subDays(new Date(), 0);
  customersToday: WritableSignal<GroupedCustomers[]> = signal([]);
  completedCustomers: WritableSignal<Customer[]> = signal([]);
  customersAtCooling: WritableSignal<Customer[]> = signal([]);
  allCustomers: Customer[] = [];
  newUnpaidBalance = 0;
  locationId: number | undefined;
  loading = true;
  comment = '';

  location: WritableSignal<LocationModel | undefined> = signal(undefined);

  selectedCustomer: WritableSignal<Customer | undefined> = signal(undefined);
  selectedAdultCount = computed(() => {
    return this.selectedCustomer() && this.settingsService.settings() ? this.getAdultCount(this.selectedCustomer()!) : undefined;
  });

  childrenCount = computed(() => {
    return this.selectedCustomer() ? this.selectedCustomer()!.family_members.length - this.selectedAdultCount()! : undefined;
  });

  selectedTodayPickUp = computed(() => {
    const selectedCustomer = this.selectedCustomer();
    return selectedCustomer ? this.getTodayPickUp(selectedCustomer) : undefined;
  });

  boxCount = computed(() => {
    return this.selectedAdultCount() ? this.getBoxCount(this.selectedAdultCount()!) : 1;
  });

  completionPercentage = computed(() => {
    const completedCustomers = this.completedCustomers().filter(c => this.getTodayPickUp(c)).length;
    const openCustomers = this.customersToday().flatMap(group => group.customers).length;
    return Math.round(completedCustomers * 100 / (completedCustomers + openCustomers));
  });

  getRemainingBoxCount = computed(() => {
    return this.customersToday()
      .flatMap(group => group.customers)
      .map(customer => this.getAdultCount(customer))
      .map(adultCount => this.getBoxCount(adultCount))
      .reduce((a, b) => a + b, 0);
  });

  getCurrentIncome = computed(() => {
    return this.completedCustomers()
      .map(customer => this.getTodayPickUp(customer)?.paid ?? 0)
      .reduce((a, b) => a + b, 0);
  });

  getRemainingCustomersCount = computed(() => {
    return this.customersToday().flatMap(group => group.customers).length;
  });

  getRemainingAdultsCount = computed(() => {
    return this.customersToday()
      .flatMap(group => group.customers)
      .map(customer => this.getAdultCount(customer))
      .reduce((a, b) => a + b, 0);
  });

  getRemainingChildrenCount = computed(() => {
    return this.customersToday()
      .flatMap(group => group.customers)
      .map(customer => customer.family_members.length - this.getAdultCount(customer))
      .reduce((a, b) => a + b, 0);
  });

  // 1 und 2 Personen >7 Jahre zahlen 3€ und bekommen eine Kiste
  // 3 und 4 Personen >7 Jahre zahlen 5€ und bekommen 2 Kisten
  // Ab 5. Person >7 Jahre zahlen 7€ und bekommen 3 Kisten
  calculatedPrice = computed(() => {
    if (this.isFreeCode()) {
      return 0;
    }
    if (this.settingsService.settings()) {
      return this.settingsService.settings()!.singleBoxPrice + (this.boxCount() - 1) * (this.settingsService.settings()!.extraBoxPrice);
    } else {
      return 0;
    }
  });

  isFreeCode = computed(() => {
    const code = this.selectedCustomer()?.code ?? this.selectedCustomer()?.code_rotation_1 ?? this.selectedCustomer()?.code_rotation_2;
    return code?.isFreeCode;
  });

  selectedTotalDue = computed(() => {
    const selectedPickUp = this.selectedTodayPickUp();
    if (selectedPickUp) {
      return Number(selectedPickUp.paid ?? 0) + Number(selectedPickUp.unpaid_balance ?? 0);
    }

    return this.calculatedPrice() + Number(this.selectedCustomer()?.pickups?.[0]?.unpaid_balance ?? 0);
  });

  async ngOnInit(): Promise<void> {
    void this.authService.fetchMe();
    this.activatedRoute.params.subscribe(async params => {
      try {
        this.locationId = params['locationId'];
        const [location] = await Promise.all([
          await this.locationService.getLocation(this.locationId!),
          await this.fetchCustomers()
        ]);
        this.location.set(location.data);

        this.loading = false;
      } catch (e) {
        console.error(e);
        this.messageService.add({
          severity: 'error',
          summary: 'Fehler beim Abrufen der Nutzer',
          detail: 'Bitte Seite neu laden.'
        });
      }
    });

    this.pickupService.socket().on('pickup', async (customerId) => {
      try {
        await this.fetchCustomers();
      } catch (error) {
        console.error(error);
        this.messageService.add({
          severity: 'error',
          summary: 'Fehler beim Abrufen der Nutzer',
          detail: 'Bitte Seite neu laden.'
        });
      }
    });

    this.pickupService.socket().on('absence', async (customerId) => {
      try {
        await this.fetchCustomers();
      } catch (error) {
        console.error(error);
        this.messageService.add({
          severity: 'error',
          summary: 'Fehler beim Abrufen der Nutzer',
          detail: 'Bitte Seite neu laden.'
        });
      }
    });
  }

  getTodayAbsence(customer: Customer) {
    return customer.absences?.find(abs => isSameDay(new Date(abs.date), this.today));
  }

  getTodayPickUp(customer: Customer) {
    return customer.pickups?.find(pickup => isSameDay(new Date(pickup.date), this.today));
  }

  getCustomerDueAmount(customer: Customer) {
    const todayPickUp = this.getTodayPickUp(customer);
    if (todayPickUp) {
      return Number(todayPickUp.paid ?? 0) + Number(todayPickUp.unpaid_balance ?? 0);
    }

    return this.settingsService.calculatePrice(customer) + Number(customer.pickups?.[0]?.unpaid_balance ?? 0);
  }

  handleCustomer(customer: Customer) {
    this.selectedCustomer.set(customer);
    this.updateFormFromCustomer(customer);
  }

  private closeCustomer() {
    this.selectedCustomer.set(undefined);
    this.newUnpaidBalance = 0;
    this.comment = '';
  }

  private updateFormFromCustomer(customer: Customer) {
    const pickup = this.getTodayPickUp(customer);
    const absence = this.getTodayAbsence(customer);
    this.newUnpaidBalance = Number(pickup?.unpaid_balance ?? 0);
    this.comment = pickup?.comment?.comment ?? absence?.comment ?? '';
  }

  private syncSelectedCustomer() {
    const selectedCustomerId = this.selectedCustomer()?.id;
    if (!selectedCustomerId) {
      return;
    }

    const currentCustomer = [
      ...this.completedCustomers(),
      ...this.customersAtCooling(),
      ...this.allCustomers,
    ].find(customer => customer.id === selectedCustomerId);

    if (currentCustomer) {
      this.selectedCustomer.set(currentCustomer);
      this.updateFormFromCustomer(currentCustomer);
    }
  }

  groupCustomersByTime(customers: Customer[]) {
    this.customersToday.set(customers.reduce((groups: GroupedCustomers[], customer: Customer) => {
      const time = customer.code?.time || customer.code_rotation_1?.time || customer.code_rotation_2?.time;

      const existingGroup = groups.find(group => group.time === time);
      if (existingGroup) {
        existingGroup.customers.push(customer);
        existingGroup.customers.sort((a, b) => {
          const aCodeValue = a.code?.value ?? a.code_rotation_1?.value ?? a.code_rotation_2?.value;
          const bCodeValue = b.code?.value ?? b.code_rotation_1?.value ?? b.code_rotation_2?.value;
          return aCodeValue - bCodeValue;
        });
      } else {
        groups.push({ time, customers: [customer] });
        groups.sort((a, b) => a.time < b.time ? -1 : 1);
      }

      return groups;
    }, []));
  }

  async fetchCustomers() {
    if (!this.locationId) {
      return;
    }
    this.completedCustomers.set(await this.pickupService.getTodayCompletedCustomers(this.locationId));
    this.customersAtCooling.set(await this.pickupService.getCustomersAtCooling(this.locationId));
    this.allCustomers = await this.pickupService.getTodayCustomers(this.locationId);
    this.groupCustomersByTime(this.allCustomers);
    this.syncSelectedCustomer();
    this.loading = false;
  }

  pickUpBox() {
    if (!this.selectedCustomer()) {
      console.error('Tried picking up non-existent selectedCustomer');
      return;
    }
    const totalDue = this.selectedTotalDue();
    const unpaidBalance = this.getNormalizedUnpaidBalance();
    const paid = totalDue - unpaidBalance;
    const boxes = this.selectedTodayPickUp()?.boxes ?? this.boxCount();
    if (this.selectedTodayPickUp()) {
      this.pickupService.socket().emit('updatePickup', this.selectedCustomer()?.id, unpaidBalance, paid, boxes, this.comment);
    } else {
      this.pickupService.socket().emit('pickup', this.selectedCustomer()?.id, unpaidBalance, paid, boxes, this.comment);
    }
    this.closeCustomer();
  }

  receivedCooling(customerId: number | undefined) {
    this.setReceivedCooling(customerId, true);
  }

  setReceivedCooling(customerId: number | undefined, receivedCooling: boolean) {
    if (!customerId) {
      return;
    }

    this.pickupService.socket().emit('setReceivedCooling', customerId, receivedCooling);
    this.closeCustomer();
  }

  deletePickup(customerId: number | undefined, event?: Event) {
    event?.stopPropagation();
    if (!customerId) {
      return;
    }

    this.confirmationService.confirm({
      target: event?.target ?? undefined,
      message: 'Diese Abholung wirklich löschen? Restschuld, Zahlung, Kühlungsstatus und Ausgabenotiz werden entfernt.',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Löschen',
      rejectLabel: 'Abbrechen',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.pickupService.socket().emit('deletePickup', customerId);
        this.closeCustomer();
      }
    });
  }

  markAbsent() {
    this.pickupService.socket().emit('absence', this.selectedCustomer()?.id, this.comment);
    this.closeCustomer();
  }

  deleteAbsence(customerId: number | undefined) {
    if (!customerId) {
      return;
    }

    this.confirmationService.confirm({
      message: 'Diese Fehlzeit wirklich löschen? Der Nutzer erscheint danach wieder in der offenen Ausgabe.',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Löschen',
      rejectLabel: 'Abbrechen',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.pickupService.socket().emit('deleteAbsence', customerId);
        this.closeCustomer();
      }
    });
  }

  getNormalizedUnpaidBalance() {
    const unpaidBalance = Number(this.newUnpaidBalance) || 0;
    return Math.min(Math.max(unpaidBalance, 0), this.selectedTotalDue());
  }

  getPaidAmount() {
    return this.selectedTotalDue() - this.getNormalizedUnpaidBalance();
  }

  getAbsenceStatusLabel(status: string) {
    return absenceStatuses.find(s => s.status === status)?.title;
  }

  getBoxCount(adultCount: number) {
    if (adultCount < 3) {
      return 1;
    } else if (adultCount < 5) {
      return 2;
    } else {
      return 3;
    }
  }

  getAdultCount(customer: Customer): number {
    const adultCountWithoutDate = customer.family_members.filter(member => member.ageType === AgeType.ADULT).length;
    return adultCountWithoutDate + customer.family_members.filter(member => member.ageType === AgeType.DATE && member.birthDate && isAfter(subYears(new Date(), this.settingsService.settings()!.adultAge), new Date(member.birthDate))).length;
  }

  filterCustomers(text: string) {
    const filteredCustomers = this.allCustomers.filter(customer =>
      customer.first_name.toLowerCase().includes(text.toLowerCase()) ||
      customer.last_name.toLowerCase().includes(text.toLowerCase()) ||
      customer.code?.value.toString().includes(text) ||
      customer.code_rotation_1?.value.toString().includes(text) ||
      customer.code_rotation_2?.value.toString().includes(text)
    );
    this.groupCustomersByTime(filteredCustomers);
  }

  protected readonly RoleEnum = RoleEnum;

  customerHasChildren(family_members: FamilyMember[]) {
    return family_members.some(mem => mem.ageType === AgeType.DATE &&
      mem.birthDate != null &&
      isAfter(new Date(mem.birthDate), subYears(new Date(), 18)));
  }
}
